import { Box, Stack } from '@mui/material';
import { colors, IconButton, Typography } from '@sweep-io/sweep-design';
import { ArrowDown, Close } from '@sweep-io/sweep-design/dist/icons';
import { useState, useMemo, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { TextFieldWithButtonV2 } from '../../../../common/ai-chat/input-atoms/TextFieldWithButtonV2';
import { AiChatMessages } from '../../../../common/ai-chat/AiChatMessages';
import { useMetadataMapping } from '../useMetadataMapping';
import { usePageHelpers } from '../usePageHelpers';
import { PanelEnvSelector } from '../../../environments/connect-env/PanelEnvSelector';
import useContainerWithScrollArrow from '../../../../../hooks/useContainerWithScrollArrow';
import { addChatMessage } from '../../../../../reducers/metadataMappingReducer';
import { ChatCompletionRequestMessageRole } from '@server/ai';
import { DateTime } from 'luxon';

interface MetadataMappingPanelProps {
  onClose: () => void;
}

export const MetadataMappingChatPanel = ({ onClose }: MetadataMappingPanelProps) => {
  const { funnelMapId } = usePageHelpers();
  const dispatch = useDispatch();
  const { messages, isLoading, error, canSendMessage, sendMessage, status, complete } =
    useMetadataMapping(funnelMapId || '');
  const [inputValue, setInputValue] = useState('');
  const hasInitializedGreetingRef = useRef(false);

  //TODO waiting for product to improve
  useEffect(() => {
    const GREETING_MESSAGE = 'Would you like to add or remove anything?';
    if (!funnelMapId || hasInitializedGreetingRef.current) return;
    hasInitializedGreetingRef.current = true;
    if (messages.length === 0) {
      dispatch(
        addChatMessage({
          funnelMapId,
          message: {
            role: ChatCompletionRequestMessageRole.ASSISTANT,
            content: GREETING_MESSAGE,
            createdAt: DateTime.now().toISO(),
          },
        }),
      );
    }
  }, [funnelMapId, messages.length, dispatch]);


  useEffect(() => {
    if (status === 'completedFetchingData') {
      complete();
    }
  }, [status, complete]);

  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const lastMessageContent = useMemo(() => lastMessage?.content, [lastMessage]);

  const { showScrollButton, scrollToBottom, containerRef } = useContainerWithScrollArrow({
    recalculateWhenThisChange: lastMessageContent,
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <PanelEnvSelector filterByEnvsUsedInCanvas />
      <Box
        sx={{
          height: '56px',
          borderBottom: `1px solid ${colors.grey[120]}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
        }}
      >
        <Typography variant="h5">Metadata Mapping Agent</Typography>
        <IconButton variant="flat" onClick={onClose} size="tiny">
          <Close />
        </IconButton>
      </Box>

      <Stack flex={1} overflow="hidden" position="relative">
        <Box
          ref={containerRef}
          sx={{
            flex: 1,
            overflow: 'auto',
            padding: '24px',
            paddingLeft: '12px',
          }}
        >
          <AiChatMessages
            containerRef={containerRef}
            messages={messages}
            renderInitialFetchMessage={false}
            renderChatPaywallBlockMessage={false}
            renderLoader={isLoading}
            errorMsg={error}
            spacerFix={35}
          />
        </Box>

        {showScrollButton && (
          <Stack
            sx={{
              position: 'absolute',
              bottom: '12px',
              width: '100%',
              alignItems: 'center',
            }}
          >
            <IconButton onClick={scrollToBottom} variant="system" size="small">
              <ArrowDown />
            </IconButton>
          </Stack>
        )}
      </Stack>

      <Box
        sx={{
          padding: '16px',
        }}
      >
        <TextFieldWithButtonV2
          value={inputValue}
          setValue={setInputValue}
          onConfirm={sendMessage}
          inputPlaceholder="Ask for changes"
          isLoading={isLoading}
          readonly={!canSendMessage}
        />
      </Box>
    </Box>
  );
};
